import React from "react";
import "../../styles/app-notification.css";
import TaskIcon from "../../assets/action-items/task-notification.svg";
import { Avatar, Radio } from "@mui/material";
import { capitalizeFirstLetter, getAvatarColor, getAvatarInitials, getMonthlyScoreColor, getMonthlyScoreLabel, getStatusOptions } from "../../utils/utils";
import EllipsedTooltip from "../Common/EllipsedTooltip";
import QuickTopicsPopper from "../Common/QuickTopicsPopper";

interface TaskNotificationItemProps {
  userName: string;
  title: string;
  timeAgo: string;
  description: string;
  avatar?: string;
  unread?: boolean;
  isDeleted?: boolean;
  onDescriptionClick?: (taskId: string, notificationId: string, isSubTask: boolean) => void;
  taskId?: string;
  notificationId?: string;
  variant?: "default" | "action-center" | "account-updates";
  onClientNameClick?: () => void;
  onActionCountClick?: () => void;
  pendingActionLabel?: string;
  showNewTag?: boolean;
  orgId?: any;
  subOrgId?: any;
  statusValue?: any;
  oldValue?: number;
  newValue?: number;
  isSubTask?: boolean;
}

const TaskNotificationItem: React.FC<TaskNotificationItemProps> = ({
  userName,
  title,
  timeAgo,
  description,
  avatar,
  unread = true,
  isDeleted,
  onDescriptionClick,
  taskId,
  notificationId,
  variant = "default",
  onClientNameClick,
  onActionCountClick,
  pendingActionLabel,
  showNewTag = false,
  orgId,
  subOrgId,
  statusValue,
  oldValue,
  newValue,
  isSubTask,
}) => {
  const isActionCenter = variant === "action-center";
  const isAccountUpdates = variant === "account-updates";
  const statusOptions = getStatusOptions();
  const status = statusOptions.find(
    (s) => s.value === statusValue
  );

  return (
    <div className="notification-row">
          <Avatar
          src={avatar}
          className="notification-avatar"
          sx={{
            bgcolor : !avatar ? getAvatarColor(userName) : "",
          }}
          >
            {getAvatarInitials(userName)}
          </Avatar>

      {/* Content */}
      <div className="notification-content">
        <div className="notification-title-wrap">
          {isActionCenter || isAccountUpdates ? (
            <div className="notification-title notification-title-action-center">
              <button
                type="button"
                className="notification-inline-button notification-client-link"
                onClick={onClientNameClick}
              >
                {capitalizeFirstLetter(userName)}
              </button>
              <QuickTopicsPopper orgId={orgId} subOrgId={subOrgId}/>
              <span>{title}</span>
              {showNewTag && <span className="notification-new-tag">New</span>}
            </div>
          ) : (
            <strong className="notification-title">
              {userName} {title}
              {status && (
                <span className="status-pill">
                  <Radio
                    checked={true} // visually always filled
                    disableRipple
                    className= "status-dot"
                    sx={{
                      margin: "0px",
                      color: status.color,
                      "& .MuiSvgIcon-root": {
                        fill: status.color, // fill inside circle
                      },
                    }}
                  />
                  {status.label}
                </span>
              )}
            </strong>
          )}
          <span className="notification-time">{timeAgo}</span>
        </div>

        <div className="notification-description">
          {isActionCenter ? (
            <button
              type="button"
              className="notification-inline-button notification-link notification-action-link"
              onClick={onActionCountClick}
            >
              {pendingActionLabel || description}
            </button>
            ) : isAccountUpdates ? (
            <div className="account-rating-change">
              <span
                className="account-rating-label"
                style={{
                  color: getMonthlyScoreColor(
                    getMonthlyScoreLabel(oldValue)
                  ),
                }}
              >
                <span
                  className="account-rating-dot"
                  style={{
                    backgroundColor: getMonthlyScoreColor(
                      getMonthlyScoreLabel(oldValue)
                    ),
                  }}
                />
                {getMonthlyScoreLabel(oldValue)}
              </span>

              <span className="account-rating-arrow">→</span>

              <span
                className="account-rating-label"
                style={{
                  color: getMonthlyScoreColor(
                    getMonthlyScoreLabel(newValue)
                  ),
                }}
              >
                <span
                  className="account-rating-dot"
                  style={{
                    backgroundColor: getMonthlyScoreColor(
                      getMonthlyScoreLabel(newValue)
                    ),
                  }}
                />
                {getMonthlyScoreLabel(newValue)}
              </span>
            </div>
          ) : (
            <>
              <img src={TaskIcon} alt="Task" />
              <EllipsedTooltip tooltipContent = {description} className={isDeleted ? "notification-deleted" : "notification-link"}>
                {isDeleted ? (
                  <span className="deleted-text">{description}</span>
                ) : (
                  <span onClick={() => onDescriptionClick?.(taskId!, notificationId!, isSubTask!)}>{description}</span>
                )}
              </EllipsedTooltip>
            </>
          )}
        </div>
      </div>

      {/* Unread dot */}
      <div className="notification-dot">
        {unread && <span className="unread-dot"></span>}
      </div>
    </div>
  );
};

export default TaskNotificationItem;
